import { Quote, ShieldCheck } from "lucide-react";
import { useList, Story } from "@/lib/api";
import { Reveal } from "@/components/site/Motion";
import { PhotoSlot } from "@/components/site/PhotoSlot";
import { Disclaimer } from "./FounderStory";

function Frame({ url, label, alt, testId }: { url?: string; label: string; alt: string; testId: string }) {
  if (!url) return <PhotoSlot label={label} className="aspect-[3/4] rounded-[20px]" testId={testId} />;
  return (
    <div className="relative aspect-[3/4] overflow-hidden rounded-[20px]">
      <img src={url} alt={alt} loading="lazy" className="h-full w-full object-cover" data-testid={testId} />
      <span className="absolute left-3 top-3 rounded-full bg-forest px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-widest text-cream">{label}</span>
    </div>
  );
}

function StoryCard({ story, index }: { story?: Story; index: number }) {
  const name = story?.name ?? "Client";
  return (
    <article data-testid={`story-card-${index}`} className="flex h-full flex-col rounded-[28px] bg-beige-soft p-4 text-charcoal transition-transform duration-700 hover:-translate-y-1.5">
      <div className="grid grid-cols-2 gap-3">
        <Frame url={story?.before_url} label="Before" alt={`${name} before`} testId={`story-${index}-before`} />
        <Frame url={story?.after_url} label="After" alt={`${name} after`} testId={`story-${index}-after`} />
      </div>
      <div className="flex flex-1 flex-col px-2 pb-2 pt-6">
        <Quote className="h-5 w-5 text-gold" />
        {story ? (
          <>
            <p className="mt-3 font-display text-2xl leading-snug text-forest">“{story.quote}”</p>
            <p className="mt-4 text-sm font-semibold">{story.name}</p>
            {(story.result_range || story.duration) && <p className="text-sm text-charcoal-soft">{[story.result_range, story.duration].filter(Boolean).join(" · ")}</p>}
          </>
        ) : (
          <>
            <p className="mt-3 font-display text-2xl leading-snug text-forest">A real client story, coming soon.</p>
            <p className="mt-2 text-sm text-charcoal-soft">Published only with the client's written consent.</p>
          </>
        )}
        <Disclaimer className="mt-auto pt-5" />
      </div>
    </article>
  );
}

export function StoriesGrid({ limit = 3 }: { limit?: number }) {
  const { items } = useList<Story>("/stories");
  const list = items.slice(0, limit);
  const slots = list.length ? list : Array.from({ length: limit }, () => undefined);
  return (
    <>
      <div className="mb-8 flex items-start gap-3 text-sm text-cream/80" data-testid="consent-policy">
        <ShieldCheck className="h-5 w-5 shrink-0 text-gold-soft" />
        <p>Consent first: no client story or photo is published without written permission.</p>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="client-stories-grid">
        {slots.map((s, i) => <Reveal key={s?.id ?? i} delay={i * 0.1}><StoryCard story={s} index={i} /></Reveal>)}
      </div>
    </>
  );
}
